import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {state} from 'lit/decorators/state.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import {msg} from '@lit/localize';

import down from '../../../icons/down';
import info from '../../../icons/info';
import next from '../../../icons/next';

import store from '../../store';

import {staggeredReveal} from '../../css/styleSets';
import {setIomoCustomColorUse} from '../iomoSlice';
import {selectIomoCustomColor, selectIomoCustomColorUse} from '../iomoSelectors';

@customElement('color-editions-select')
export class ColorEditionsSelect extends connect(store)(LitElement) {

	@property({type: Array})
	options = [];

	@property({type: Function})
	selected = null;

	@property({type: Function})
	canBeSelected = () => true;

	@property({type: Function})
	isSatisfied = null;

	@property({type: String})
	action = '';

	@property({type: Function})
	callback = null;

	@state({type: String})
	activeEdition = null;

	static styles = [
		staggeredReveal,
		css`
			:host {
				background: var(--color-widget-background);
				display: flex;
				flex-direction: column;
			}

			.title {
				display: flex;
				justify-content: space-between;
				align-items: center;
				font: var(--font-widget-title);
				color: var(--color-widget-text);
				margin-bottom: 12px;
			}

			.info {
				color: var(--color-widget-infoicon);
				background-color: transparent;
				cursor: pointer;
				transition: background-color 0.4s, color 0.4s;
				border: none;
				padding: 0;
			}

			.info:hover {
				color: var(--color-widget-text);
			}

			.info svg path {
				fill: currentColor;
				stroke: currentColor;
			}

			.info svg circle {
				stroke: currentColor;
			}

			.colorEditions {
				display: flex;
				flex-direction: column;
			}

			.colorEdition {
				display: flex;
				flex-direction: row;
				color: var(--color-widget-text);
				font: var(--font-widget-title);
				height: 121px;
				cursor: pointer;
				transition: height 0.4s, opacity 0.4s;
				overflow: hidden;
				box-shadow: 1px 0 0 0 var(--color-widget-border),
				0 1px 0 0 var(--color-widget-border),
				1px 1px 0 0 var(--color-widget-border),
				1px 0 0 0 var(--color-widget-border) inset,
				0 1px 0 0 var(--color-widget-border) inset;
			}

			.colorEditionThumbnail {
				background: #999;
				width: 48px;
				height: 121px;
				aspect-ratio: 1;
				flex-basis: 48px;
				box-shadow: 1px 0 0 0 var(--color-widget-border),
				0 1px 0 0 var(--color-widget-border),
				1px 1px 0 0 var(--color-widget-border),
				1px 0 0 0 var(--color-widget-border) inset,
				0 1px 0 0 var(--color-widget-border) inset;
			}

			.thumbnail {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}

			.colorEditionText {
				display: flex;
				flex-direction: column;
			}
			.colorEditionLabelAndToogle {
				display: flex;
				flex-direction: row;
				padding: var(--small-padding) 0;
			}
			.colorEditionLabel {
				color: var(--color-widget-text);
				font: var(--font-widget-big-label);
				flex-basis: 100%;
				align-content: center;
				padding: 0 var(--very-small-padding);
			}
			.colorEditionDescription {
				padding: 0 var(--very-small-padding);
				font: var(--font-widget-label);
			}

			.colorEditionToggle {
				color: var(--color-widget-text);
				font: var(--font-widget-title);
				flex-basis: 32x;
				align-content: center;
				padding: 0 var(--very-small-padding);
			}

			.colorEditionToggle svg {
				transition: transform 0.3s ease-out;
				transform: rotate(0deg);

			}

			.active .colorEditionToggle svg {
				transform: rotate(180deg);
			}

			.colorEdition.inactive {
				height: 0;
				opacity: 0;
				pointer-events: none;
			}

			.colorThemes {
				display: none;
				flex-direction: column;
			}

			.colorThemes.active {
				display: flex;
				margin-bottom: var(--very-small-padding);
			}

			.colorThemes.notSatisfied {
				border-left-color: var(--color-warn);
			}

			.satisfied {
				color: var(--color-text-widget-title);
			}

			.notSatisfied {
				color: var(--color-warn);
			}
		`];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(s) {
		this.lang = s.status.lang;
		this.loggedIn = s.userProfile.data.loggedIn;
		this.customColor = selectIomoCustomColor(s);
		this.useCustomColor = selectIomoCustomColorUse(s);
	}

	firstUpdated() {
		const selectedId = this.selected(store.getState());
		const selectedEdition = this.options.find(edition =>
			edition.colorThemes.some(colorTheme => colorTheme.id === selectedId)
		);

		if (selectedEdition) this.activeEdition = selectedEdition.id;
	}

	toggleEdition(editionId) {
		this.activeEdition = this.activeEdition === editionId ? null : editionId;
	}

	handleEditionKeydown(event, editionId) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			this.toggleEdition(editionId);
		}
	}
	render() {
		const s = store.getState();
		return html`
			<div>
				<div class="colorEditions">
					${repeat(this.options,
						(edition) => edition.id,
						(edition) => {
							const active = this.activeEdition === edition.id;
							const inactive = this.activeEdition !== null && this.activeEdition !== edition.id;
							return html`
								<div class="colorEdition ${classMap({active: active, inactive: inactive})}"
								     role="button"
								     tabindex="0"
								     aria-expanded="${active}"
								     aria-controls="colorThemes-${edition.id}"
								     @click=${() => this.toggleEdition(edition.id)}
								     @keydown=${event => this.handleEditionKeydown(event, edition.id)}>
									<div class="colorEditionThumbnail">
										<img class="thumbnail" alt"" src="${edition.thumbnail}">
									</div>
									<div class="colorEditionText">
										<div class="colorEditionLabelAndToogle">
											<div class="colorEditionLabel">${edition.label[this.lang]}</div>
											<div class="colorEditionToggle">${next}</div>
										</div>
										<div class="colorEditionDescription">${edition.description[this.lang]}</div>
									</div>
								</div>

								<div id="colorThemes-${edition.id}" class="colorThemes ${classMap({active: active})}"
								     ?hidden=${!active}>
									${repeat(edition.colorThemes,
										(colorTheme) => colorTheme.id,
										(colorTheme) => html`
											${when(!colorTheme.requireLogin || (colorTheme.requireLogin && this.loggedIn), () => html`
													<color-editions-select-item .option=${colorTheme}
													                            .canBeSelected=${this.canBeSelected(s, colorTheme.id)}
													                            selected=${this.selected(s)}
													                            .hideDisabled=${this.hideDisabled}
													                            action="${this.action}">
													</color-editions-select-item>`,
												() => null
											)}
										`)}
								</div>
							`;
						})}
				</div>
			</div>
		`;
	}
}
